/**
 * NotificationsBell — Cloche de notifications dans le header
 *
 * Dropdown avec liste des notifications récentes.
 * Badge avec nombre de non-lues.
 */

import { useState, useEffect, useRef } from "react";
import { api } from "../lib/api";

interface Notification {
  id: string;
  type: string;
  severity: "info" | "warn" | "error" | "success";
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
}

const SEVERITY_ICON: Record<string, string> = {
  info: "ℹ️",
  warn: "⚠️",
  error: "🔴",
  success: "✅",
};

const TYPE_ICON: Record<string, string> = {
  mission: "🛰",
  cicd: "🚀",
  security: "🛡",
  system: "⚙",
  approval: "👑",
};

export default function NotificationsBell() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const ref = useRef<HTMLDivElement>(null);

  const loadNotifications = async () => {
    try {
      setNotifications((await api.notifications.list({ unreadOnly: false, limit: 20 })) as Notification[]);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30_000);
    return () => clearInterval(interval);
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAllRead = async () => {
    try {
      await api.notifications.markAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {
      // ignore
    }
  };

  const markOneRead = async (id: string) => {
    try {
      await api.notifications.markRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n))
      );
    } catch {
      // ignore
    }
  };

  const filtered = filter === "unread" ? notifications.filter((n) => !n.read) : notifications;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {/* Bell button */}
      <div
        id="notif-bell"
        onClick={() => setOpen(!open)}
        style={{
          cursor: "pointer",
          fontSize: "18px",
          padding: "8px",
          borderRadius: "12px",
          background: open ? "rgba(255,255,255,0.08)" : "transparent",
          transition: "background 0.15s",
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        🔔
        {unreadCount > 0 && (
          <span
            style={{
              background: "var(--rust)",
              color: "white",
              fontSize: "10px",
              fontWeight: 700,
              padding: "2px 6px",
              borderRadius: "999px",
              fontFamily: "var(--mono)",
              minWidth: "18px",
              textAlign: "center",
            }}
          >
            {unreadCount}
          </span>
        )}
      </div>

      {/* Dropdown */}
      {open && (
        <div
          id="notif-dropdown"
          className="cmd-question-overlay"
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            marginTop: "8px",
            width: "380px",
            maxWidth: "90vw",
            maxHeight: "500px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            borderRadius: "20px",
            border: "1px solid var(--line)",
            background: "rgba(18,22,41,0.98)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
            backdropFilter: "blur(10px)",
            zIndex: 1000,
          }}
        >
          {/* Header */}
          <div style={{
            padding: "14px 16px",
            borderBottom: "1px solid var(--line)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
            <strong style={{ fontSize: "15px" }}>🔔 Notifications</strong>
            <div style={{ display: "flex", gap: "6px" }}>
              <span
                className={`chip ${filter === "all" ? "active" : ""}`}
                style={{ fontSize: "11px", cursor: "pointer", padding: "4px 10px", borderRadius: "999px", background: filter === "all" ? "rgba(183,148,246,0.14)" : "rgba(255,255,255,0.04)", border: "1px solid var(--line)" }}
                onClick={() => setFilter("all")}
              >
                Toutes
              </span>
              <span
                className={`chip ${filter === "unread" ? "active" : ""}`}
                style={{ fontSize: "11px", cursor: "pointer", padding: "4px 10px", borderRadius: "999px", background: filter === "unread" ? "rgba(183,148,246,0.14)" : "rgba(255,255,255,0.04)", border: "1px solid var(--line)" }}
                onClick={() => setFilter("unread")}
              >
                Non lues ({unreadCount})
              </span>
            </div>
          </div>

          {/* List */}
          <div style={{ overflow: "auto", flex: 1, padding: "8px", display: "grid", gap: "6px" }}>
            {filtered.length === 0 && (
              <div style={{ padding: "30px", textAlign: "center", color: "var(--muted)", fontSize: "13px" }}>
                Aucune notification
              </div>
            )}
            {filtered.map((n) => (
              <div
                key={n.id}
                onClick={() => !n.read && markOneRead(n.id)}
                style={{
                  padding: "12px",
                  borderRadius: "14px",
                  background: n.read ? "rgba(0,0,0,0.14)" : "rgba(183,148,246,0.06)",
                  border: `1px solid ${n.read ? "transparent" : "rgba(183,148,246,0.16)"}`,
                  cursor: n.read ? "default" : "pointer",
                  display: "grid",
                  gap: "4px",
                  transition: "background 0.15s",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ fontSize: "14px" }}>{TYPE_ICON[n.type] || "📌"} {SEVERITY_ICON[n.severity]}</span>
                  <strong style={{ fontSize: "13px", flex: 1 }}>{n.title}</strong>
                  {!n.read && <span style={{ width: "8px", height: "8px", borderRadius: "999px", background: "var(--purple)", flexShrink: 0 }} />}
                </div>
                <div style={{ fontSize: "12px", color: "var(--muted)" }}>{n.message}</div>
                <div style={{ fontSize: "10px", color: "var(--muted)", fontFamily: "var(--mono)" }}>
                  {new Date(n.createdAt).toLocaleString("fr-FR")}
                </div>
              </div>
            ))}
          </div>

          {/* Footer */}
          {unreadCount > 0 && (
            <div style={{ padding: "10px 16px", borderTop: "1px solid var(--line)", textAlign: "center" }}>
              <button className="btn" style={{ fontSize: "11px" }} onClick={markAllRead}>
                ✓ Tout marquer comme lu
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
